import React from 'react';
import { motion } from 'framer-motion';
import { MiniDjayPrompt } from './MiniDjayPrompt';
import type { DjayEmotion, DjayVariant } from '../../mascot';
interface WorldZoneProps {
  title: string;
  icon: React.ElementType;
  accentColor: string;
  children: React.ReactNode;
  djayMessage?: string;
  djayEmotion?: DjayEmotion;
  djayVariant?: DjayVariant;
  isLast?: boolean;
  className?: string;
}
export function WorldZone({
  title,
  icon: Icon,
  accentColor,
  children,
  djayMessage,
  djayEmotion = 'happy',
  djayVariant = 'default',
  isLast = false,
  className = ''
}: WorldZoneProps) {
  // Extract colors from accentColor (e.g., 'bg-blue-100 text-blue-600')
  const bgClass = accentColor.split(' ').find(c => c.startsWith('bg-')) || 'bg-indigo-100';
  const textClass = accentColor.split(' ').find(c => c.startsWith('text-')) || 'text-indigo-600';
  // Create a vibrant gradient background for the zone
  const gradientFrom = bgClass.replace('-100', '-50');
  const gradientTo = bgClass.replace('-100', '-100/30');
  return <div className={`relative ${className}`} data-id="element-2317">
      <div className={`relative rounded-[2.5rem] p-6 sm:p-10 bg-gradient-to-b ${gradientFrom} ${gradientTo} border-2 border-white shadow-sm overflow-hidden`} data-id="element-2318">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" data-id="element-2319" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/30 rounded-full blur-2xl translate-y-1/4 -translate-x-1/4 pointer-events-none" data-id="element-2320" />

        {/* Header Area */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10 relative z-10" data-id="element-2321">
          <div className="flex items-center gap-5" data-id="element-2322">
            <div className={`w-16 h-16 rounded-3xl bg-white flex items-center justify-center shadow-md transform -rotate-6`} data-id="element-2323">
              <Icon className={`w-8 h-8 ${textClass}`} strokeWidth={2.5} data-id="element-2324" />
            </div>
            <div data-id="element-2325">
              <h2 className={`text-3xl font-black ${textClass.replace('-600', '-900')} tracking-tight drop-shadow-sm`} data-id="element-2326">
                {title}
              </h2>
              {djayMessage && <div className="mt-3 hidden sm:block" data-id="element-2327">
                  <MiniDjayPrompt message={djayMessage} emotion={djayEmotion} variant={djayVariant} data-id="element-2328" />
                </div>}
            </div>
          </div>
        </div>

        {/* Mobile Djay Prompt */}
        {djayMessage && <div className="mb-8 sm:hidden relative z-10" data-id="element-2329">
            <MiniDjayPrompt message={djayMessage} emotion={djayEmotion} variant={djayVariant} data-id="element-2330" />
          </div>}

        {/* Content Area */}
        <div className="relative z-10" data-id="element-2331">{children}</div>
      </div>

      {/* Visual Connector between zones */}
      {!isLast && <div className="relative h-16 w-full flex justify-center items-center" data-id="element-2332">
          <div className="h-full w-2 border-l-4 border-dashed border-slate-200/80" data-id="element-2333" />
          <motion.div className="absolute w-4 h-4 rounded-full bg-slate-200" animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-2334" />
        </div>}
    </div>;
}